import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function WhyMedilineSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.3 });
  const { ref: timelineRef, isVisible: timelineVisible } = useScrollAnimation({ threshold: 0.2 });

  const benefits = [
    {
      number: '01',
      title: 'No Capital Investment',
      description: 'Lease premium imaging equipment without the multi-million dollar purchase cost or depreciation burden'
    },
    {
      number: '02',
      title: 'Rapid Deployment',
      description: '48–72 hour emergency deployment capability with complete site setup and infrastructure support'
    },
    {
      number: '03',
      title: 'Flexible Lease Terms',
      description: 'Short-term, long-term, and custom leasing arrangements — from days to multi-year agreements'
    },
    {
      number: '04',
      title: 'Equipment Maintained',
      description: 'Preventative maintenance and uptime monitoring included — we keep the hardware performing at peak'
    },
    {
      number: '05',
      title: 'Nationwide Coverage',
      description: 'Equipment delivery and logistics support across the country with experienced deployment teams'
    }
  ];

  return (
    <section className="py-16 sm:py-24 lg:py-36 px-4 sm:px-6 bg-[#F7F9FB] overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 sm:mb-18 transition-all duration-700 ease-out ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-slate-blue text-[13px] uppercase tracking-[2px] font-medium mb-3">
            WHY LEASE WITH MEDILINE
          </p>
          <h2 className="text-navy text-[26px] sm:text-[34px] lg:text-[40px] font-semibold leading-tight">
            The Smarter Way to Access Imaging Equipment
          </h2>
        </div>

        {/* Timeline */}
        <div ref={timelineRef} className="relative mt-10 sm:mt-20">
          {/* Connecting Line - Desktop only */}
          <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200 hidden lg:block overflow-hidden">
            <div
              className={`h-full bg-slate-blue transition-all duration-1500 ease-out ${
                timelineVisible ? 'w-full' : 'w-0'
              }`}
            ></div>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`relative flex flex-col items-center text-center transition-all duration-700 ease-out ${
                  timelineVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                } ${index === 4 ? 'sm:col-span-2 lg:col-span-1' : ''}`}
                style={{ transitionDelay: timelineVisible ? `${300 + index * 150}ms` : '0ms' }}
              >
                {/* Node Circle */}
                <div
                  className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white border-4 border-slate-blue flex items-center justify-center mb-5 sm:mb-8 relative z-10 transition-all duration-500 hover:scale-110 hover:bg-slate-blue group cursor-pointer ${
                    timelineVisible ? 'scale-100' : 'scale-0'
                  }`}
                  style={{ transitionDelay: timelineVisible ? `${200 + index * 150}ms` : '0ms' }}
                >
                  <span className="text-slate-blue text-xl sm:text-2xl font-bold transition-colors duration-300 group-hover:text-white">
                    {benefit.number}
                  </span>
                </div>

                {/* Content */}
                <h3 className="text-navy text-lg sm:text-xl font-semibold mb-2 sm:mb-3">
                  {benefit.title}
                </h3>
                <p className="text-[#5A6C7D] text-sm sm:text-[15px] leading-relaxed max-w-[280px] sm:max-w-[220px]">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
